import { Icons } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import { SPACING_3 } from '@mnd-frontend/ui/shared/tokens';
import { type Dispatch, type SetStateAction, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import mndUrl, { SubDomain } from '../../../../../lib/mndUrl';
import routes from '../../../../../routes';
import { type Item, type MainMenuItem, MenuState, type Pressroom } from '../types';
import DesktopNavListGroup from './DesktopNavListGroup';
import LogoOrPressroomSelector from './LogoOrPressroomSelector';
import NavigationItem from './NavigationItem';
import TrialMenuItem from './TrialMenuItem';

const Nav = styled.nav`
  background-color: transparent;
  position: fixed;
  z-index: 1000;
  top: 0;
  bottom: 0;
  width: var(--mnd-navigation-width);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  height: 100%;
  padding: 1.5rem 0;

  ${media.compactDesktopNavigation} {
    padding-bottom: 1rem;
  }
  > ul:last-child {
    margin-bottom: 1rem;
    ${media.compactDesktopNavigation} {
      margin-bottom: 0;
    }
  }
`;

const TopNavGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  overflow-x: hidden;
  scrollbar-width: thin;
  overflow-y: auto;
`;

const TrialItemWrapper = styled.div`
  margin-bottom: ${SPACING_3};

  > * {
    position: unset;
  }
`;

interface DesktopNavigationProps {
  loading: boolean;
  navigationItems: MainMenuItem[];
  renderPressroomSelector: boolean;
  isSubNavOpen: MenuState | null;
  trial: boolean | undefined;
  pressrooms: Pressroom[];
  topGroup: MenuState[];
  isOnlyJournalist: boolean;
  journalistItems: Item[];
  newsroomLogo?: string | null;
  pressroomName: string;
  siteCode?: string;
  setIsSubNavOpen: Dispatch<SetStateAction<MenuState | null>>;
}

const DesktopNavigation = ({
  loading,
  navigationItems,
  renderPressroomSelector,
  isSubNavOpen,
  trial,
  pressrooms,
  topGroup,
  isOnlyJournalist,
  journalistItems,
  newsroomLogo,
  pressroomName,
  siteCode,
  setIsSubNavOpen,
}: DesktopNavigationProps) => {
  const { t } = useTranslation();

  const navListRef = useRef<HTMLDivElement>(null);
  const topGroupItems = navigationItems.filter(item => topGroup.includes(item.menuState));
  const bottomGroupItems = navigationItems.filter(item => !topGroup.includes(item.menuState));

  return (
    <Nav aria-label={t('aria_label_main_navigation')} ref={navListRef}>
      <TopNavGroupWrapper>
        {trial && (
          <TrialItemWrapper>
            <TrialMenuItem
              isOpen={isSubNavOpen === MenuState.Trial}
              open={() => setIsSubNavOpen(MenuState.Trial)}
              close={() => setIsSubNavOpen(state => (state === MenuState.Trial ? null : state))}
              positionAbsolute={true}
            />
          </TrialItemWrapper>
        )}
        <DesktopNavListGroup
          id="top-group"
          group="top"
          groupedNavigationItems={topGroupItems}
          subNavState={[isSubNavOpen, setIsSubNavOpen]}
          extraNavItem={
            <LogoOrPressroomSelector
              pressroomName={pressroomName}
              loading={loading}
              newsroomLogo={newsroomLogo}
              pressroomSelector={renderPressroomSelector}
              siteCode={siteCode}
              itemData={{
                heading: t('menu_pressroom_heading'),
                menuState: MenuState.PressroomSelector,
                newsroomLogo,
                items:
                  pressrooms.map(p => {
                    return {
                      text: p.name,
                      url: mndUrl(SubDomain.Publish, routes.dashboard({ pressroomId: p.id })),
                      siteCode: p.siteCode,
                    };
                  }) ?? [],
              }}
              isSubNavOpen={isSubNavOpen}
              open={() => setIsSubNavOpen(MenuState.PressroomSelector)}
              close={() =>
                setIsSubNavOpen(state => (state === MenuState.PressroomSelector ? null : state))
              }
            />
          }
        />
      </TopNavGroupWrapper>
      <DesktopNavListGroup
        id="bottom-group"
        group="bottom"
        groupedNavigationItems={bottomGroupItems}
        subNavState={[isSubNavOpen, setIsSubNavOpen]}
        extraNavItem={
          isOnlyJournalist ? (
            <NavigationItem
              topGroup={false}
              itemData={{
                heading: 'Journalist',
                menuState: MenuState.User,
                icon: <Icons.User />,
                items: journalistItems,
              }}
              isSubNavOpen={isSubNavOpen}
              open={() => setIsSubNavOpen(MenuState.User)}
              close={() => setIsSubNavOpen(state => (state === MenuState.User ? null : state))}
            />
          ) : undefined
        }
      />
    </Nav>
  );
};

export default DesktopNavigation;
